import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Alert, AlertDescription } from './ui/alert';
import { Badge } from './ui/badge';
import { Loader2, CheckCircle, Clock, XCircle } from 'lucide-react';
import { useAirdrop } from '../hooks/useAirdrop';

export const AirdropApplicationForm = ({ walletAddress, onApplicationSubmitted }) => {
  const [email, setEmail] = useState('');
  const [localWalletAddress, setLocalWalletAddress] = useState(walletAddress || '');
  const [existingApplication, setExistingApplication] = useState(null);
  const [checking, setChecking] = useState(false);
  
  const { 
    applyForAirdrop, 
    checkWalletStatus, 
    activeCampaign, 
    loadingSubmission,
    loadCampaigns 
  } = useAirdrop();

  useEffect(() => {
    loadCampaigns();
  }, [loadCampaigns]);

  const checkApplication = useCallback(async () => {
    if (!localWalletAddress || localWalletAddress.trim().length < 32) return;
    
    setChecking(true);
    try {
      const application = await checkWalletStatus(localWalletAddress);
      setExistingApplication(application);
    } catch (error) {
      console.error('Error checking application:', error);
      setExistingApplication(null);
    } finally {
      setChecking(false);
    }
  }, [localWalletAddress, checkWalletStatus]);

  useEffect(() => {
    if (!localWalletAddress || localWalletAddress.trim().length < 32) {
      setExistingApplication(null);
      setChecking(false);
      return;
    }

    const timeoutId = setTimeout(() => {
      checkApplication();
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [localWalletAddress, checkApplication]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!localWalletAddress) return;

    try {
      const application = await applyForAirdrop(localWalletAddress, email || undefined);
      setExistingApplication(application);
      if (onApplicationSubmitted) {
        onApplicationSubmitted(application);
      }
    } catch (error) {
      // Error handled in the hook
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'pending':
        return <Clock className="h-4 w-4" />;
      case 'approved':
      case 'distributed':
        return <CheckCircle className="h-4 w-4" />;
      case 'rejected':
        return <XCircle className="h-4 w-4" />;
      default:
        return <Clock className="h-4 w-4" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'approved':
        return 'bg-blue-100 text-blue-800';
      case 'distributed':
        return 'bg-green-100 text-green-800';
      case 'rejected':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  if (!activeCampaign) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Airdrop Application</CardTitle>
          <CardDescription>No active airdrop campaign available at the moment.</CardDescription>
        </CardHeader>
      </Card>
    );
  }

  if (!activeCampaign.isActive) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Airdrop Application</CardTitle>
          <CardDescription>The airdrop campaign is currently inactive.</CardDescription>
        </CardHeader>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Apply for Airdrop</CardTitle>
        <CardDescription>{activeCampaign.description}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="p-4 bg-blue-50 rounded-lg">
          <h4 className="font-semibold text-blue-900">{activeCampaign.name}</h4>
          <p className="text-sm text-blue-700">
            Token Amount: {activeCampaign.tokenAmount.toLocaleString()} tokens per recipient
          </p>
          <p className="text-sm text-blue-700">
            Remaining: {(activeCampaign.totalSupply - activeCampaign.distributedAmount).toLocaleString()} tokens
          </p>
        </div>

        {checking && localWalletAddress.trim().length >= 32 && (
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <Loader2 className="h-4 w-4 animate-spin" />
            Checking application status...
          </div>
        )}

        {existingApplication && (
          <Alert>
            <div className="flex items-center gap-2">
              {getStatusIcon(existingApplication.status)}
              <AlertDescription>
                <div className="flex items-center justify-between">
                  <span>
                    Your application status: 
                    <Badge className={`ml-2 ${getStatusColor(existingApplication.status)}`}>
                      {existingApplication.status.toUpperCase()}
                    </Badge>
                  </span>
                  {existingApplication.status === 'distributed' && existingApplication.txSignature && (
                    <Button
                      variant="link"
                      size="sm"
                      onClick={() => window.open(
                        `https://solscan.io/tx/${existingApplication.txSignature}`,
                        '_blank'
                      )}
                    >
                      View Transaction
                    </Button>
                  )}
                </div>
                <div className="text-xs text-gray-500 mt-1">
                  Applied: {new Date(existingApplication.appliedAt.toDate()).toLocaleDateString()}
                  {existingApplication.distributedAt && (
                    <span className="ml-2">
                      | Distributed: {new Date(existingApplication.distributedAt.toDate()).toLocaleDateString()}
                    </span>
                  )}
                </div>
              </AlertDescription>
            </div>
          </Alert>
        )}

        {!existingApplication && (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="walletAddress">Wallet Address *</Label>
              <Input
                id="walletAddress"
                type="text"
                value={localWalletAddress}
                onChange={(e) => setLocalWalletAddress(e.target.value)}
                placeholder="Enter your Solana wallet address"
                required
                disabled={!!walletAddress}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="email">Email (Optional)</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email for updates"
              />
            </div>

            {activeCampaign.requirements && activeCampaign.requirements.length > 0 && (
              <div className="p-3 bg-gray-50 rounded-lg">
                <h5 className="font-medium text-gray-900 mb-2">Requirements:</h5>
                <ul className="text-sm text-gray-600 space-y-1">
                  {activeCampaign.requirements.map((req, index) => (
                    <li key={index} className="flex items-start gap-2">
                      <span className="text-gray-400">•</span>
                      {req}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <Button 
              type="submit" 
              className="w-full" 
              disabled={loadingSubmission || !localWalletAddress}
            >
              {loadingSubmission ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Submitting Application...
                </>
              ) : (
                'Apply for Airdrop'
              )}
            </Button>
          </form>
        )}

        {existingApplication && existingApplication.status === 'pending' && (
          <div className="text-center py-4">
            <p className="text-sm text-gray-600">
              Your application is under review. You will be notified once it's processed.
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
